import axios from "axios";
import { BASE_URL } from "../../../../../../utils";

const API_BASE_URL = "http://172.16.2.47:4000/api";

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token
// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const token = user?.token; // safely access user.token

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {

    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  getProfile: () => api.get("/auth/me"),
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.put("/auth/change-password", data),
};



export const inventoryAPI = {
  getDashboardCounts: () => api.get("/inv/inventory/dashboard-counts"),
  getStockSummary: (params) => api.get("/inv/inventory/stock-summary", { params }),
  getExpiryAlerts: (params) => api.get("/inv/inventory/expiry-alerts", { params }),
  adjustStock: (data) => api.post("/inv/inventory/adjust-stock", data),
  getRackUtilization: () => api.get("/inv/inventory/rack-utilization"),
  transferRack: (data) => api.put("/inv/inventory/transfer-rack", data),
  getComprehensiveInventory: (params) =>
    api.get("/inv/inventory/comprehensive", { params }),
  getLowStockAlerts: (params) =>
    api.get("/inv/inventory/low-stock-alerts", { params }),
};



export const returnAPI = {
  getAll: (params) => api.get("/returns", { params }),
  getById: (id) => api.get(`/returns/${id}`),
  create: (data) => api.post("/returns", data),
  approve: (id, data) => api.put(`/returns/${id}/approve`, data),
  reject: (id, data) => api.put(`/returns/${id}/reject`, data),
  complete: (id) => api.put(`/returns/${id}/complete`),
  getBySaleInvoice: (saleInvoiceId) =>
    api.get(`/returns/sale/${saleInvoiceId}`),
  checkEligibility: (saleInvoiceId) =>
    api.get(`/returns/check-eligibility/${saleInvoiceId}`),
};

// Purchase API
export const purchaseAPI = {
  getAll: (params) => api.get("/inv/purchases", { params }),
  getById: (id) => api.get(`/inv/purchases/${id}`),
  create: (data) => api.post("/inv/purchases", data),
  update: (id, data) => api.put(`/inv/purchases/${id}`, data),
  delete: (id) => api.delete(`/inv/purchases/${id}`),
  receive: (id, data) => api.put(`/inv/purchases/${id}/receive`, data),
  partialReceive: (id, data) =>
    api.put(`/inv/purchases/${id}/partial-receive`, data),
  updateStatus: (id, data) => api.put(`/inv/purchases/${id}/status`, data),
  getGRN: (purchaseOrderId) => api.get(`/inv/purchases/${purchaseOrderId}/grn`),
  downloadGRN: (purchaseOrderId) => api.get(`/inv/purchases/${purchaseOrderId}/grn/download`, { responseType: 'blob' }),
};


// Suppliers API
export const suppliersAPI = {
  // Basic CRUD
  getAll: (params) => api.get("/inv/suppliers", { params }),
  getById: (id) => api.get(`/inv/suppliers/${id}`),
  create: (data) => api.post("/inv/suppliers", data),
  update: (id, data) => api.put(`/inv/suppliers/${id}`, data),
  delete: (id) => api.delete(`/inv/suppliers/${id}`),

  // Get suppliers by item
  getSuppliersByItem: (itemId, params) =>
    api.get(`/inv/suppliers/items/${itemId}`, { params }),

  // Performance & Analytics
  getPerformance: (id, params) =>
    api.get(`/inv/suppliers/${id}/performance`, { params }),
  getAnalytics: (id, params) =>
    api.get(`/inv/suppliers/${id}/analytics`, { params }),

  // Documents
  getDocuments: (id) => api.get(`/inv/suppliers/${id}/documents`),
  uploadDocument: (id, formData) =>
    api.post(`/inv/suppliers/${id}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  downloadDocument: (id, documentId) =>
    api.get(`/inv/suppliers/${id}/documents/${documentId}`),
  deleteDocument: (id, documentId) =>
    api.delete(`/inv/suppliers/${id}/documents/${documentId}`),

  // Communications
  getCommunications: (id) => api.get(`/inv/suppliers/${id}/communications`),
  addCommunication: (id, data) =>
    api.post(`/inv/suppliers/${id}/communications`, data),
  deleteCommunication: (id, communicationId) =>
    api.delete(`/inv/suppliers/${id}/communications/${communicationId}`),

  // Items (supplier's item catalog)
  getItems: (id) => api.get(`/inv/suppliers/${id}/items`),
  addItem: (id, data) => api.post(`/inv/suppliers/${id}/items`, data),
  updateItem: (id, itemId, data) =>
    api.put(`/inv/suppliers/${id}/items/${itemId}`, data),
  removeItem: (id, itemId) => api.delete(`/inv/suppliers/${id}/items/${itemId}`),

  // Payments
  getPayments: (id) => api.get(`/inv/suppliers/${id}/payments`),
  addPayment: (supplierId, paymentData) =>
    api.post(`/inv/suppliers/${supplierId}/payments`, paymentData),
  getFinancialSummary: (supplierId) =>
    api.get(`/inv/suppliers/${supplierId}/financial-summary`),
  getPaymentAnalytics: (supplierId, params = {}) =>
    api.get(`/inv/suppliers/${supplierId}/payments/analytics`, { params }),

  // GRNs
  getGRNs: (id, params = {}) => api.get(`/inv/suppliers/${id}/grns`, { params }),
  getUnpaidGRNs: (id) => api.get(`/inv/suppliers/${id}/unpaid-grns`),

  // Returns
  getReturns: (id, params = {}) =>
    api.get(`/inv/suppliers/${id}/returns`, { params }),
  createReturn: (id, data) => api.post(`/inv/suppliers/${id}/returns`, data),
  updateReturnStatus: (id, returnId, data) =>
    api.put(`/inv/suppliers/${id}/returns/${returnId}/status`, data),
  getReturnStatistics: (id) => api.get(`/inv/suppliers/${id}/returns/statistics`),
  getReturnAnalytics: (id, params = {}) =>
    api.get(`/inv/suppliers/${id}/returns/analytics`, { params }),

  // Batch search for returns
  searchBatchesForReturn: (supplierId, searchParams) =>
    api.get(`/inv/suppliers/${supplierId}/returns/available-batches`, {
      params: searchParams,
    }),

  // Create return from selected batches
  createReturnFromBatches: (supplierId, returnData) =>
    api.post(`/inv/suppliers/${supplierId}/returns/from-batches`, returnData),

  // Process return (approve and adjust stock)
  processReturn: (supplierId, returnId, data) =>
    api.post(`/inv/suppliers/${supplierId}/returns/${returnId}/process`, data),
};

// Rack API
export const racksAPI = {
  getAll: (params) => api.get("/inv/racks", { params }),
  getById: (id) => api.get(`/inv/racks/${id}`),
  create: (data) => api.post("/inv/racks", data),
  update: (id, data) => api.put(`/inv/racks/${id}`, data),
  delete: (id) => api.delete(`/inv/racks/${id}`),
  getUtilization: () => api.get("/inv/racks/utilization"),
  getAvailable: () => api.get("/inv/racks/available"),
  transferStock: (data) => api.post("/inv/racks/transfer-stock", data),
};

// Reports API
export const reportsAPI = {
  getDepartmentConsumptionReport: (params) =>
    api.get("/inv/reports/department-consumption", { params }),
  getInventoryReport: (params) => api.get("/inv/reports/inventory", { params }),
  getItemUsageReport: (params) => api.get("/inv/reports/item-usage", { params }),

  getPurchaseOrdersReport: (params) =>
    api.get("/inv/reports/purchase-orders", { params }),
  getSupplierPerformanceReport: (params) =>
    api.get("/inv/reports/supplier-performance", { params }),
  getStockMovementReport: (params) =>
    api.get("/inv/reports/stock-movement", { params }),
  getExpiryReport: (params) => api.get("/inv/reports/expiry-alerts", { params }),
  getReturnsReport: (params) => api.get("/inv/reports/returns", { params }),
  // Legacy endpoints (deprecated)
  getSalesReport: (params) => api.get("/inv/reports/sales", { params }),
  getProfitLossReport: (params) => api.get("/reports/profit-loss", { params }),

  // Export functions
  exportReport: (reportType, params) => api.get(`/inv/reports/${reportType}/export`, { 
    params, 
    responseType: 'blob' 
  }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get("/inv/analytics/dashboard"),
  getSalesTrends: (params) => api.get("/inv/analytics/sales-trends", { params }),
};

// Items API
export const itemsAPI = {
  getAll: (params) => api.get("/items", { params }),
  getById: (id) => api.get(`/items/${id}`),
  create: (data) => api.post("/items", data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  search: (query, params = {}) =>
    api.get("/items/search", { params: { q: query, ...params } }),
  getStock: (id) => api.get(`/items/${id}/stock`),
  generateBarcode: (id) => api.post(`/items/${id}/barcode`),
  getStockHistory: (id, params) =>
    api.get(`/items/${id}/stock-history`, { params }),
  getBatches: (id) => api.get(`/items/${id}/batches`),
  getBySupplier: (supplierId, params = {}) =>
    api.get(`/items/supplier/${supplierId}`, { params }),
};

// Departments API (replaces Patients API)
export const departmentsAPI = {
  getAll: (params) => api.get("/departments", { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post("/departments", data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  search: (query, params = {}) =>
    api.get("/departments/search", { params: { q: query, ...params } }),
  getStock: (id) => api.get(`/departments/${id}/stock`),
  getConsumption: (id, params = {}) =>
    api.get(`/departments/${id}/consumption`, { params }),
};

// Item Issues API (replaces Sales API)
export const itemIssuesAPI = {
  getAll: (params) => api.get("/item-issues", { params }),
  getById: (id) => api.get(`/item-issues/${id}`),
  create: (data) => api.post("/item-issues", data),
  update: (id, data) => api.put(`/item-issues/${id}`, data),
  approve: (id, data = {}) => api.put(`/item-issues/${id}/approve`, data),
  issue: (id, data = {}) => api.put(`/item-issues/${id}/issue`, data),
  cancel: (id, data = {}) => api.put(`/item-issues/${id}/cancel`, data),
  checkAvailability: (data) =>
    api.post("/item-issues/check-availability", data),
  getReturnableItems: (id) => api.get(`/item-issues/${id}/returnable`),
};

// Item Issue Returns API (replaces Return API)
export const itemIssueReturnsAPI = {
  getAll: (params) => api.get("/item-issue-returns", { params }),
  getById: (id) => api.get(`/item-issue-returns/${id}`),
  create: (data) => api.post("/item-issue-returns", data),
  approve: (id, data = {}) =>
    api.put(`/item-issue-returns/${id}/approve`, data),
  reject: (id, data) => api.put(`/item-issue-returns/${id}/reject`, data),
  complete: (id, data = {}) =>
    api.put(`/item-issue-returns/${id}/complete`, data),
  getByItemIssue: (itemIssueId) =>
    api.get(`/item-issue-returns/item-issue/${itemIssueId}`),
  checkEligibility: (itemIssueId) =>
    api.get(`/item-issue-returns/check-eligibility/${itemIssueId}`),
};

// Department Stock API (NEW)
export const departmentStockAPI = {
  getAll: (params) => api.get("/department-stock", { params }),
  getByDepartment: (departmentId, params = {}) =>
    api.get(`/department-stock/department/${departmentId}`, { params }),
  getByItem: (itemId, params = {}) =>
    api.get(`/department-stock/item/${itemId}`, { params }),
  getLowStock: (params = {}) =>
    api.get("/department-stock/low-stock", { params }),
  updateLevels: (id, data) => api.put(`/department-stock/${id}/levels`, data),
  getConsumptionRate: (id, params = {}) =>
    api.get(`/department-stock/${id}/consumption`, { params }),
};

export default api;
